import { FC, useState } from 'react'
import { Row, Col, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { MeetingDashboardFragment, EmployeeDisplayTinyInfo } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { PerModal } from '../../../components/custom_components/Modal'
import PerDatePicker from '../../../components/custom_components/DatePicker'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerTimePicker from '../../../components/custom_components/TimePicker'
import useRescheduleMeeting from '../../../graphql/meeting/hooks/useRescheduleMeeting'
import { MeetingInviteDurationSelect } from '../../../components/custom_components/InviteModalContent/MeetingInviteDurationSelect'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import { InviteModalState } from '../../../modules/meeting/invite/MeetingInviteModal'
import { InviteModalMeetingOverlap } from '../../../components/custom_components/InviteModalContent/InviteModalMeetingOverlap'
import { InviteModalContentUtils } from '../../../components/custom_components/InviteModalContent/inviteModalContentUtils'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'

export interface MeetingRescheduleModalProps {
  meeting: MeetingDashboardFragment
  visible: boolean
  reviewerId: number
  onCancel: () => void
}

export const MeetingRescheduleModal: FC<MeetingRescheduleModalProps> = ({ meeting, visible, onCancel, reviewerId }) => {
  const employee = { ...meeting.employee }
  const [rescheduleDate, setRescheduleDate] = useState<string>(meeting.displayDate)
  const [duration, setDuration] = useState<number>(meeting.duration ? meeting.duration : 3600)
  const [location, setLocation] = useState<string | undefined>(meeting.location ? meeting.location : undefined)
  const [isProcessing, setIsProcessing] = useState(false)

  const [t] = useTranslation()
  const { user } = useAuth()
  const { rescheduleMeeting } = useRescheduleMeeting(reviewerId)

  const firstColumnSpan = 6
  const secondColumnSpan = 17

  const tinyEmployee: EmployeeDisplayTinyInfo = {
    id: employee.id,
    firstName: employee.legalFirstName,
    lastName: employee.legalLastName,
  }

  const meetingState: InviteModalState = {
    appraisalSeason: meeting.appraisalSeason,
    isLoadingASOptions: true,
    meetingId: meeting.id,
    employee: tinyEmployee,
    isLoadingInviteOptions: true,
    isLoadingNonParticipantsOptions: true,
    meetingType: meeting.type,
    isAppraisalEnabled: true,
    meetingDate: rescheduleDate,
    duration: duration,
    location: !!meeting.location ? meeting.location : undefined,
    additionalParticipants: meeting.nonParticipants,
    isLoadingSkillStatusData: false,
    skillStatusData: undefined,
  }

  const inviteModalContentUtils = new InviteModalContentUtils({
    timeZoneAndLocale: user,
    appraisalSeason: meeting.appraisalSeason,
  })

  const cleanAndCloseModal = () => {
    onCancel()
  }

  const saveRescheduleMeeting = async () => {
    setIsProcessing(true)
    const sanitizedLocation = !!location ? (location.trim().length > 0 ? location.trim() : undefined) : undefined
    const result = await rescheduleMeeting({ meetingId: meeting.id, meetingDate: rescheduleDate, duration, location: sanitizedLocation })
    if (result && !result.errors) {
      cleanAndCloseModal()
      showNotification({
        type: 'success',
        message: t('rescheduleModal.notifications.reschedule.successTitle'),
        description: t('rescheduleModal.notifications.reschedule.successDescription'),
      })
    } else {
      showNotification({
        type: 'error',
        message: t('rescheduleModal.notifications.reschedule.errorTitle'),
        description: t('rescheduleModal.notifications.reschedule.errorDescription'),
      })
    }
    setIsProcessing(false)
  }

  const invalidDateTime =
    rescheduleDate === '' ||
    rescheduleDate === null ||
    rescheduleDate === undefined ||
    inviteModalContentUtils.isMeetingDateNotAllowed(rescheduleDate)

  const disabledInviteButton = invalidDateTime

  const disabledInviteButtonTooltip = () => {
    if (invalidDateTime) {
      return t(`meetingInvite.invalidDateTimeTooltip`)
    } else {
      return undefined
    }
  }

  return (
    <PerModal
      visible={visible}
      onCancel={() => {
        cleanAndCloseModal()
      }}
      title={{ heading: t(`meetingInvite.headlineReschedule`) }}
      footer={{
        justify: 'end',
        customBtns: [
          {
            text: t(`meetingInvite.doRescheduleButton`),
            onClick: (e) => {
              e.preventDefault() // We should refactor button to not emit native event!
              saveRescheduleMeeting()
            },
            color: 'success',
            icon: 'rescedule_fill',
            disabled: disabledInviteButton || isProcessing,
            tooltip: disabledInviteButtonTooltip(),
            dataE2eAttr: 'confirm',
            isProcessing: {
              active: isProcessing,
              text: '',
            },
          },
        ],
      }}
    >
      <Row type="flex" align="middle">
        <Col span={firstColumnSpan}>{t('meetingInvite.meetingInviteEmployee')}</Col>
        <Col span={secondColumnSpan}>{computeFullname(user.nameDisplayPreference, employee.legalFirstName, employee.legalLastName)}</Col>
      </Row>
      <PerSpacer mt="s">
        <Row type="flex" align="middle">
          <Col span={firstColumnSpan}>{t('meetingInvite.appraisalSeason')}</Col>
          <Col span={secondColumnSpan}>{meeting.appraisalSeason.year}</Col>
        </Row>
      </PerSpacer>
      <PerSpacer mt="s">
        <Row type="flex" align="middle">
          <Col span={firstColumnSpan}>{t('meetingInvite.meetingType')}</Col>
          <Col span={secondColumnSpan}>{t(`meetingTypes.${meeting.type}`)}</Col>
        </Row>
      </PerSpacer>
      <PerSpacer mt="s">
        <Row type="flex" align="middle">
          <Col span={firstColumnSpan}>{t('meetingInvite.meetingDate')}</Col>
          <Col span={secondColumnSpan}>
            <PerDatePicker
              inputDate={rescheduleDate}
              onDateChanged={(utcDateChanged) => {
                !!utcDateChanged && setRescheduleDate(utcDateChanged)
              }}
              timeZoneAndLocale={user}
              dateFormat={'DMY'}
              disabled={false}
              disabledDate={inviteModalContentUtils.isMeetingDateNotAvailable}
            />
            <InviteModalMeetingOverlap
              duration={meetingState.duration}
              meetingDate={meetingState.meetingDate}
              meetingId={meetingState.meetingId}
            />
          </Col>
        </Row>
      </PerSpacer>
      <PerSpacer mt="s">
        <Row type="flex" align="middle">
          <Col span={firstColumnSpan}>{t('meetingInvite.meetingTime')}</Col>
          <Col span={secondColumnSpan}>
            <PerTimePicker
              inputDate={rescheduleDate}
              onTimeChanged={(utcTimeChanged) => {
                !!utcTimeChanged && setRescheduleDate(utcTimeChanged)
              }}
              timeZoneAndLocale={user}
              timeFormat={'HH:mm'}
              minuteStep={15}
              hideDisabledOptions={true}
            />
          </Col>
        </Row>
      </PerSpacer>
      <PerSpacer mt="s">
        <Row type="flex" align="middle">
          <Col span={firstColumnSpan}>{t('meetingInvite.duration')}</Col>
          <Col span={secondColumnSpan}>
            <MeetingInviteDurationSelect duration={duration} onChangeDuration={(duration) => setDuration(duration)} />
          </Col>
        </Row>
      </PerSpacer>
      <PerSpacer mt="s">
        <Row type="flex" align="middle">
          <Col span={firstColumnSpan}>{t('meetingInvite.location')}</Col>
          <Col span={secondColumnSpan}>
            <Input
              type="text"
              name="location"
              value={location || ''}
              onChange={({ target: { value } }) => setLocation(value)}
              placeholder={t('meetingInvite.locationPlaceholder')}
              data-e2e-modal-meeting-location
            />
          </Col>
        </Row>
      </PerSpacer>
      {meeting.nonParticipants.length !== 0 && (
        <PerSpacer mt="s">
          <Row type="flex" align="middle">
            <Col span={firstColumnSpan}>{t('meetingInvite.additionalParticipants')}</Col>
            <Col span={secondColumnSpan}>
              {computeFullname(user.nameDisplayPreference, meeting.nonParticipants[0].firstName, meeting.nonParticipants[0].lastName)}
            </Col>
          </Row>
        </PerSpacer>
      )}
    </PerModal>
  )
}
